import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'

import { DocumentRecord } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import {
    DeleteContractInputDto,
    GenerateContractInputDto,
    GetContractsInputDto,
    UpdateContractInputDto,
    UpdateContractNumberInputDto,
    UploadFinalContractInputDto,
    ContractUnion,
} from '@synapex/shared2/gateway-rest-api'

import { apiQueryKey } from '../../query-key'
import { type OmitWwld } from '../../utils'
import {
    apiDeleteContract,
    apiGenerateContract,
    apiGetContracts,
    apiUpdateContract,
    apiUpdateContractNumber,
    apiUploadContractFile,
} from '../api'

export const useGetContracts = (
    {
        workspace_id,
        workspace_type,
        contragentDistributorId,
    }: GetContractsInputDto,
    options?: Omit<UseQueryOptions<ContractUnion[], Error>, 'queryKey'>
) => {
    return useQuery<ContractUnion[], Error>({
        queryKey: apiQueryKey.contracts(
            {
                workspace_id,
                workspace_type,
            },
            contragentDistributorId
        ),
        queryFn: () =>
            apiGetContracts({
                workspace_id,
                workspace_type,
                contragentDistributorId,
            }),
        ...options,
    })
}

export const useGenerateContract = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<void, Error, GenerateContractInputDto>,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['generate-contract'],
        mutationFn: (input: OmitWwld<GenerateContractInputDto>) =>
            apiGenerateContract({ ...wwld, ...input }) as Promise<void>,
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(wwld, input.ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
        ...options,
    })
}

export const useUpdateContract = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<void, Error, UpdateContractInputDto>,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['update-contract'],
        mutationFn: (input: OmitWwld<UpdateContractInputDto>) =>
            apiUpdateContract({ ...wwld, ...input }) as Promise<void>,
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(wwld, input.ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
        ...options,
    })
}

export const useUpdateContractNumber = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<DocumentRecord, Error, UpdateContractNumberInputDto>,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['update-contract-number'],
        mutationFn: (input: OmitWwld<UpdateContractNumberInputDto>) =>
            apiUpdateContractNumber({
                ...wwld,
                ...input,
            }) as Promise<DocumentRecord>,
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(wwld, input.ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
        ...options,
    })
}

export const useDeleteContract = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<void, Error, DeleteContractInputDto>,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['delete-contract'],
        mutationFn: (input: OmitWwld<DeleteContractInputDto>) =>
            apiDeleteContract({ ...wwld, ...input }) as Promise<void>,
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(wwld, input.ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(),
                predicate: (query) => {
                    if (!query.state.data) {
                        return false
                    }
                    const queryData = query.state.data as {
                        draft_contract_id?: number
                    }
                    return (
                        queryData.draft_contract_id === input.documentRecordId
                    )
                },
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdHistory(wwld),
            })
        },
        ...options,
    })
}

export const useUploadContractFile = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<
            void,
            Error,
            OmitWwld<UploadFinalContractInputDto> & { file: File }
        >,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['upload-file'],
        mutationFn: (
            input: OmitWwld<UploadFinalContractInputDto> & {
                file: File
            }
        ) => apiUploadContractFile({ ...wwld, ...input }) as Promise<void>,

        onSuccess(_, input) {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(wwld, input.ordContragentId),
            })

            // Invalidate only marking forms that have matching draftContractId
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(),
                predicate: (query) => {
                    if (!query.state.data) {
                        return false
                    }
                    const queryData = query.state.data as {
                        draft_contract_id?: number
                    }
                    return queryData.draft_contract_id === input.draftContractId
                },
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdHistory(wwld),
            })
        },
        ...options,
    })
}
